import { useCallback, useEffect, useRef } from 'react';
import { useMutation, type DefaultError, type MutateOptions, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import { actionKey } from './actionKey';

// RC-107 — um pedido por vez. O botão só desabilita quando a tela redesenha com o pedido em
// andamento; o segundo clique chega antes e chama `mutate` de novo. O React Query só executa os
// retornos passados ao último `mutate`: o sucesso do primeiro (que fecharia a janela) nunca roda,
// e o conflito do segundo aparece como se nada tivesse sido gravado.
//
// Aqui, um `mutate` que chega com outro ainda sem resposta não manda pedido novo: fica esperando
// o resultado do que já saiu. Quando ele chega, roda os retornos de quem chamou por último, uma
// vez. Isso também cobre a janela que pede a prévia ao abrir: em desenvolvimento o React monta,
// desmonta e monta de novo, o efeito chama duas vezes, e o resultado do único pedido é entregue.
// `mutateAsync` continua como é.
export function useSingleFlightMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const mutation = useMutation(options);
  type Flight = { handlers?: MutateOptions<TData, TError, TVariables, TContext> };
  const flight = useRef<Flight | null>(null);
  const { mutateAsync } = mutation;
  const mutate = useCallback(
    (variables: TVariables, handlers?: MutateOptions<TData, TError, TVariables, TContext>) => {
      if (flight.current) {
        flight.current.handlers = handlers;
        return;
      }
      const current: Flight = { handlers };
      flight.current = current;
      // Os retornos por chamada recebem os dados e as variáveis; o contexto de `onMutate` não passa por aqui.
      type Loose = Partial<Record<'onSuccess' | 'onError' | 'onSettled', (...args: unknown[]) => void>>;
      const land = (): Loose | undefined => {
        flight.current = null;
        return current.handlers as Loose | undefined;
      };
      mutateAsync(variables).then(
        (data) => {
          const last = land();
          last?.onSuccess?.(data, variables, undefined, undefined);
          last?.onSettled?.(data, null, variables, undefined, undefined);
        },
        (error: TError) => {
          const last = land();
          last?.onError?.(error, variables, undefined, undefined);
          last?.onSettled?.(undefined, error, variables, undefined, undefined);
        },
      );
    },
    [mutateAsync],
  );
  return { ...mutation, mutate } as UseMutationResult<TData, TError, TVariables, TContext>;
}

// Story 65.37 — a mesma trava para o resto da aplicação, por AÇÃO IGUAL. Nas telas antigas um
// hook só serve várias linhas (inativar o serviço A, depois o B): travar por hook engoliria o
// segundo clique e entregaria a ele o resultado do primeiro (RC-118). Aqui, um `mutate` só é
// fundido com o que está a caminho quando manda o mesmo conteúdo (`actionKey`); ação diferente
// sai, e cada uma recebe o seu resultado. `mutateAsync` segue a mesma regra: o segundo pedido
// igual recebe a mesma resposta do primeiro, sem ir ao servidor. Os retornos por chamada só
// rodam com a tela ainda montada, como no React Query. `key` troca o que define a ação quando
// o conteúdo muda a cada clique (um horário de agora, por exemplo).
// A guarda `scripts/governance/single-flight-check.py` cobra este hook (ou o de cima) em toda
// gravação; o que precisa ficar sem trava leva `// single-flight-exempt: <motivo>`.
export function useSameActionMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  lock: { key?: (variables: TVariables) => unknown } = {},
): UseMutationResult<TData, TError, TVariables, TContext> {
  const mutation = useMutation(options);
  type Handlers = MutateOptions<TData, TError, TVariables, TContext>;
  type Flight = { promise: Promise<TData>; handlers?: Handlers };
  const flights = useRef(new Map<string, Flight>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const keyOf = useRef(lock.key);
  keyOf.current = lock.key;
  const { mutateAsync: send } = mutation;

  /** O pedido igual que já saiu, ou um novo. `fresh` diz se este chamado é quem enviou. */
  const flightOf = useCallback(
    (variables: TVariables): { flight: Flight; fresh: boolean } => {
      const key = actionKey(keyOf.current ? keyOf.current(variables) : variables);
      const flying = flights.current.get(key);
      if (flying) return { flight: flying, fresh: false };
      const flight: Flight = { promise: send(variables) };
      flights.current.set(key, flight);
      const land = () => {
        if (flights.current.get(key) === flight) flights.current.delete(key);
      };
      flight.promise.then(land, land);
      return { flight, fresh: true };
    },
    [send],
  );

  const mutate = useCallback(
    (variables: TVariables, handlers?: Handlers) => {
      const { flight, fresh } = flightOf(variables);
      // Mesmo pedido já a caminho: nada sai de novo; vale o retorno de quem chamou por último.
      flight.handlers = handlers ?? flight.handlers;
      if (!fresh) return;
      type Loose = Partial<Record<'onSuccess' | 'onError' | 'onSettled', (...args: unknown[]) => void>>;
      const last = (): Loose | undefined => (mounted.current ? (flight.handlers as Loose | undefined) : undefined);
      flight.promise.then(
        (data) => {
          const mine = last();
          mine?.onSuccess?.(data, variables, undefined, undefined);
          mine?.onSettled?.(data, null, variables, undefined, undefined);
        },
        (error: TError) => {
          const mine = last();
          mine?.onError?.(error, variables, undefined, undefined);
          mine?.onSettled?.(undefined, error, variables, undefined, undefined);
        },
      );
    },
    [flightOf],
  );

  const mutateAsync = useCallback((variables: TVariables) => flightOf(variables).flight.promise, [flightOf]);

  return { ...mutation, mutate, mutateAsync } as UseMutationResult<TData, TError, TVariables, TContext>;
}
